/*  ========================================================================
    # Section - Heroshowreel
    ========================================================================  */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import bem from '../../helpers/bem';
import useLocalization from '../../hooks/useLocalization';
import useTranslation from '../../hooks/useTranslation';
import useTabVisible from '../../hooks/useTabVisible';

const HeroShowreel = ({ lightboxVideo }) => {
  const __ = useTranslation();

  // useRef
  const rootRef = useRef(null);
  const dialogRef = useRef(null);
  const lightboxVideoRef = useRef(null);
  const heroVideosRef = useRef([]);
  const heroVideosInViewRef = useRef(true);
  const [videoPlay, setVideoPlay] = useState(false);
  const [pauseByTabLeaving, setPauseByTabLeaving] = useState(false);
  const [lightboxVideoStartedOnce, setLightboxVideoStartedOnce] = useState(false);

  // Determine if the tab is visible
  const tabVisible = useTabVisible();

  // Icons
  const localize = useLocalization();
  const iconPlay = `${localize('svgSprite')}#play`;
  const iconClose = `${localize('svgSprite')}#cross`;

  // Play/pause the background showreel video(s) based on viewport visibility, tab focus
  // and lightbox state, to avoid decoding video that isn't actually on screen.
  const syncHeroVideoPlayback = useCallback(() => {
    const shouldPlay = heroVideosInViewRef.current && tabVisible && !videoPlay;
    heroVideosRef.current.forEach(video => {
      if (shouldPlay) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [tabVisible, videoPlay]);

  // Keep a ref to the latest sync function so the IntersectionObserver callback below
  // (attached once on mount) never closes over stale tabVisible/videoPlay values.
  const syncHeroVideoPlaybackRef = useRef(syncHeroVideoPlayback);
  useEffect(() => {
    syncHeroVideoPlaybackRef.current = syncHeroVideoPlayback;
  }, [syncHeroVideoPlayback]);

  // Locate the background video(s) belonging to this section instance (rendered server-side,
  // outside the React tree) and observe their viewport visibility.
  useEffect(() => {
    const section = rootRef.current?.closest('.s-hero-showreel');
    heroVideosRef.current = section
      ? Array.from(section.querySelectorAll('.s-hero-showreel__video'))
      : [];

    if (!heroVideosRef.current.length || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        heroVideosInViewRef.current = entry.isIntersecting;
        syncHeroVideoPlaybackRef.current();
      },
      { threshold: 0 }
    );
    heroVideosRef.current.forEach(video => observer.observe(video));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    syncHeroVideoPlayback();
  }, [syncHeroVideoPlayback]);

  // Handle dialog toggle
  const handleLightboxToggle = () => {
    if (dialogRef.current && dialogRef.current.open) {
      setVideoPlay(false);
      lightboxVideoRef.current.pause();
      dialogRef.current.close();
      document.body.style = null;
    } else {
      setVideoPlay(true);
      if (!lightboxVideoStartedOnce) {
        setLightboxVideoStartedOnce(true);
        lightboxVideoRef.current.src = lightboxVideoRef.current.dataset.src;
      }
      dialogRef.current.showModal();
      lightboxVideoRef.current.currentTime = 0;
      lightboxVideoRef.current.play();
      document.body.style = 'overflow: hidden';
    }
  };

  // Pause video and close lightbox if tab is not visible
  useEffect(() => {
    if (!tabVisible && videoPlay) {
      lightboxVideoRef.current.pause();
      setVideoPlay(false);
      setPauseByTabLeaving(true);
    } else if (pauseByTabLeaving) {
      setVideoPlay(true);
      setPauseByTabLeaving(false);
      if (dialogRef.current && dialogRef.current.open) {
        lightboxVideoRef.current.play();
      }
    }
  }, [tabVisible]);

  return (
    <>
      <div
        ref={rootRef}
        className={bem('s-hero-showreel', 'playbutton-wrapper')}
        onClick={handleLightboxToggle}
      >
        <button
          title={__('play-video-title')}
          aria-label={__('play-video-title')}
          className={`${bem('c-button', '', { black: true, icon: true })} ${bem(
            's-hero-showreel',
            'video-control'
          )}`}
        >
          <svg viewBox='0 0 200 200'>
            <use href={iconPlay} />
          </svg>
        </button>
      </div>
      <dialog
        className={bem('s-hero-showreel', 'lightbox')}
        ref={dialogRef}
        onClose={() => (document.body.style = null)} //eslint-disable-line
      >
        <button
          title={__('lightbox-close-button')}
          aria-label={__('lightbox-close-button')}
          className={`${bem('s-hero-showreel', 'lightbox-control')} ${bem('c-button', '', {
            icon: true,
            white: true
          })}`}
          onClick={handleLightboxToggle}
        >
          <svg viewBox='0 0 200 200'>
            <use href={iconClose} />
          </svg>
        </button>
        <div className={bem('s-hero-showreel', 'lightbox-video')}>
          <picture>
            <video
              ref={lightboxVideoRef}
              data-src={lightboxVideo.url}
              title={lightboxVideo.description}
              controls
            />
          </picture>
        </div>
      </dialog>
    </>
  );
};

HeroShowreel.propTypes = {
  lightboxVideo: PropTypes.object
};

export default HeroShowreel;
